import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '../../utils/api';
import { InfoTag } from '../../components/ui/InfoTag';

const PRESET_QUERIES = [
  'best AI tools for small business',
  'best AI writing tools 2026',
  'Jasper AI alternatives',
  'HubSpot vs ActiveCampaign for SMB',
  'best email marketing AI tool',
  'AI tools for marketing agencies',
  'what is the best AI tool for content creation',
  'Semrush alternatives 2026',
  'AI tools under $50 per month',
  'how to implement AI in a small business',
];

const statusColor = (score) => {
  if (score >= 80) return 'var(--accent-secondary)';
  if (score >= 50) return 'var(--accent-primary)';
  return '#ef4444';
};

const defaultSettings = {
  freshness_days: 90,
  low_score_threshold: 55,
  auto_attach_notes: true,
  prioritize_ai_overviews: true,
  include_llm_answer_block: true,
  require_source_citations: true,
  target_platforms: ['chatgpt', 'perplexity', 'ai-overview', 'copilot'],
};

const AEODashboard = () => {
  const [scores, setScores] = useState([]);
  const [citationStats, setCitationStats] = useState(null);
  const [staleTools, setStaleTools] = useState([]);
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [analysisBusy, setAnalysisBusy] = useState(false);
  const [manualAnalysis, setManualAnalysis] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [autoRunSummary, setAutoRunSummary] = useState(null);

  const [citationForm, setCitationForm] = useState({
    query: PRESET_QUERIES[0],
    platform: 'chatgpt',
    cited: true,
    position: 1,
    pageUrl: '',
    notes: '',
  });

  const [analysisForm, setAnalysisForm] = useState({
    page_type: 'best',
    slug: '',
    persist_notes: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [scoresRes, citationRes, staleRes, settingsRes] = await Promise.all([
        adminApi.getAeoScores(),
        adminApi.getAeoCitationStats(7),
        adminApi.getStaleSeoTools(90),
        adminApi.getAeoSettings(),
      ]);

      setScores(scoresRes.data?.pages || []);
      setCitationStats(citationRes.data || null);
      setStaleTools(staleRes.data?.items || []);
      setSettings({ ...defaultSettings, ...(settingsRes.data || {}) });
    } catch (error) {
      console.error('Failed to load AEO dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const queryRows = useMemo(() => {
    const statsMap = citationStats?.summary || {};
    return PRESET_QUERIES.map((query) => ({
      query,
      chatgpt: statsMap.chatgpt?.citationRate || 0,
      perplexity: statsMap.perplexity?.citationRate || 0,
      aiOverview: statsMap['ai-overview']?.citationRate || 0,
      copilot: statsMap.copilot?.citationRate || 0,
    }));
  }, [citationStats]);

  const pageOptions = useMemo(
    () => scores.slice(0, 300).map((item) => ({
      label: `${item.pageType}/${item.slug} (${item.score})`,
      value: `${item.pageType}|${item.slug}`,
    })),
    [scores]
  );

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await adminApi.updateAeoSettings(settings);
      await load();
    } catch (error) {
      console.error('Failed to save AEO settings', error);
      alert('Failed to save AEO settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleCitationLog = async () => {
    try {
      await adminApi.createAeoCitationLog({
        ...citationForm,
        testedAt: new Date().toISOString(),
      });
      setCitationForm((prev) => ({ ...prev, notes: '' }));
      const updatedStats = await adminApi.getAeoCitationStats(7);
      setCitationStats(updatedStats.data || null);
    } catch (error) {
      console.error('Failed to log citation', error);
      alert('Failed to log citation event');
    }
  };

  const handleAnalyzePage = async () => {
    if (!analysisForm.slug) {
      alert('Choose a page first');
      return;
    }

    setAnalysisBusy(true);
    try {
      const response = await adminApi.analyzeAeoPage(analysisForm);
      setManualAnalysis(response.data || null);
      setNotesDraft(response.data?.suggestedNotes || '');
      await load();
    } catch (error) {
      console.error('Failed to analyze page', error);
      alert('Failed to analyze page');
    } finally {
      setAnalysisBusy(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!analysisForm.slug) {
      alert('Pick and analyze a page first');
      return;
    }
    try {
      await adminApi.updateAeoPageNotes({
        page_type: analysisForm.page_type,
        slug: analysisForm.slug,
        notes: notesDraft,
      });
      await load();
      alert('Notes saved');
    } catch (error) {
      console.error('Failed to save notes', error);
      alert('Failed to save notes');
    }
  };

  const handleAutoAnalyzeLowScorePages = async () => {
    setAnalysisBusy(true);
    try {
      const response = await adminApi.analyzeAeoLowScorePages({
        limit: 25,
        threshold: settings.low_score_threshold,
      });
      setAutoRunSummary(response.data || null);
      await load();
    } catch (error) {
      console.error('Failed to auto-analyze low score pages', error);
      alert('Failed to auto-analyze low score pages');
    } finally {
      setAnalysisBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="terminal-grid p-6" data-testid="aeo-dashboard-loading">
        <p style={{ color: 'var(--text-secondary)' }}>Loading AEO dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="aeo-dashboard">
      <div className="terminal-grid p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>AEO Control Center</h2>
            <InfoTag label="AEO settings" description="Configure content freshness thresholds, AEO scoring weights, and automation targets" variant="primary" />
          </div>
          <button className="btn-primary" onClick={handleSaveSettings} disabled={savingSettings}>
            {savingSettings ? 'Saving...' : 'Save AEO Settings'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Freshness days
            <input type="number" min="7" max="365" value={settings.freshness_days} onChange={(event) => setSettings((prev) => ({ ...prev, freshness_days: Number(event.target.value) }))} className="w-full mt-1 px-3 py-2 bg-transparent border border-white/20 text-white" />
          </label>
          <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Low score threshold
            <input type="number" min="10" max="95" value={settings.low_score_threshold} onChange={(event) => setSettings((prev) => ({ ...prev, low_score_threshold: Number(event.target.value) }))} className="w-full mt-1 px-3 py-2 bg-transparent border border-white/20 text-white" />
          </label>
          <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Target platforms (csv)
            <input value={(settings.target_platforms || []).join(', ')} onChange={(event) => setSettings((prev) => ({ ...prev, target_platforms: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) }))} className="w-full mt-1 px-3 py-2 bg-transparent border border-white/20 text-white" />
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          {[
            ['auto_attach_notes', 'Auto-attach notes'],
            ['prioritize_ai_overviews', 'Prioritize AI Overviews'],
            ['include_llm_answer_block', 'Include answer-first block'],
            ['require_source_citations', 'Require source citations'],
          ].map(([key, label]) => (
            <label key={key} className="inline-flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={!!settings[key]} onChange={(event) => setSettings((prev) => ({ ...prev, [key]: event.target.checked }))} />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="terminal-grid p-6">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Manual Page Analysis + Notes</h3>
            <InfoTag label="Page analysis" description="Manually audit any page for AEO compliance including freshness, FAQ coverage, and citation signals" variant="primary" />
          </div>
          <div className="grid grid-cols-1 gap-3 mb-3">
            <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Page type
              <select value={analysisForm.page_type} onChange={(event) => setAnalysisForm((prev) => ({ ...prev, page_type: event.target.value }))} className="w-full mt-1 px-3 py-2 bg-transparent border border-white/20 text-white">
                <option value="best">Best Pages</option>
                <option value="alternatives">Alternatives Pages</option>
                <option value="blog">Blog Posts</option>
              </select>
            </label>
            <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Choose page
              <select
                value={`${analysisForm.page_type}|${analysisForm.slug}`}
                onChange={(event) => {
                  const [pageType, slug] = event.target.value.split('|');
                  setAnalysisForm((prev) => ({ ...prev, page_type: pageType, slug: slug || '' }));
                }}
                className="w-full mt-1 px-3 py-2 bg-transparent border border-white/20 text-white"
              >
                <option value={`${analysisForm.page_type}|`}>Select page</option>
                {pageOptions.filter((option) => option.value.startsWith(`${analysisForm.page_type}|`)).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="inline-flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={analysisForm.persist_notes} onChange={(event) => setAnalysisForm((prev) => ({ ...prev, persist_notes: event.target.checked }))} />
              Persist generated notes automatically
            </label>
            <div className="flex gap-2">
              <button className="btn-primary" onClick={handleAnalyzePage} disabled={analysisBusy}>{analysisBusy ? 'Analyzing...' : 'Analyze Page'}</button>
              <button className="btn-secondary" onClick={handleAutoAnalyzeLowScorePages} disabled={analysisBusy}>Auto-analyze Low Score Pages</button>
            </div>
            {autoRunSummary && (
              <p className="text-sm" style={{ color: 'var(--accent-primary)' }}>
                Auto run complete: updated {autoRunSummary.updated} pages at threshold {autoRunSummary.threshold}.
              </p>
            )}
          </div>

          {manualAnalysis && (
            <div className="mt-4 p-4" style={{ border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)' }}>
              <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                Score: <strong style={{ color: statusColor(manualAnalysis.score) }}>{manualAnalysis.score}</strong>
              </p>
              <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Action pointers</p>
              <ul className="text-sm space-y-1 mb-3" style={{ color: 'var(--text-secondary)' }}>
                {(manualAnalysis.pointers || []).map((pointer, index) => <li key={index}>- {pointer}</li>)}
              </ul>
              <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Page notes
                <textarea value={notesDraft} onChange={(event) => setNotesDraft(event.target.value)} rows={10} className="w-full mt-1 px-3 py-2 bg-transparent border border-white/20 text-white" />
              </label>
              <button className="btn-primary mt-3" onClick={handleSaveNotes}>Save Notes</button>
            </div>
          )}
        </div>

        <div className="terminal-grid p-6">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Citation Logger</h3>
            <InfoTag label="Track AI citations" description="Log when your content is cited by AI tools and search engines to measure answer engine presence" variant="primary" />
          </div>
          <div className="grid grid-cols-1 gap-3">
            <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Query
              <input value={citationForm.query} onChange={(event) => setCitationForm((prev) => ({ ...prev, query: event.target.value }))} className="w-full mt-1 px-3 py-2 bg-transparent border border-white/20 text-white" />
            </label>
            <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Platform
              <select value={citationForm.platform} onChange={(event) => setCitationForm((prev) => ({ ...prev, platform: event.target.value }))} className="w-full mt-1 px-3 py-2 bg-transparent border border-white/20 text-white">
                <option value="chatgpt">ChatGPT</option>
                <option value="perplexity">Perplexity</option>
                <option value="ai-overview">AI Overview</option>
                <option value="copilot">Copilot</option>
              </select>
            </label>
            <label className="inline-flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={citationForm.cited} onChange={(event) => setCitationForm((prev) => ({ ...prev, cited: event.target.checked }))} />
              Page cited in result
            </label>
            <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Position
              <input type="number" min="1" value={citationForm.position} onChange={(event) => setCitationForm((prev) => ({ ...prev, position: Number(event.target.value) }))} className="w-full mt-1 px-3 py-2 bg-transparent border border-white/20 text-white" />
            </label>
            <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Page URL
              <input value={citationForm.pageUrl} onChange={(event) => setCitationForm((prev) => ({ ...prev, pageUrl: event.target.value }))} className="w-full mt-1 px-3 py-2 bg-transparent border border-white/20 text-white" />
            </label>
            <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Notes
              <textarea value={citationForm.notes} onChange={(event) => setCitationForm((prev) => ({ ...prev, notes: event.target.value }))} rows={3} className="w-full mt-1 px-3 py-2 bg-transparent border border-white/20 text-white" />
            </label>
            <button className="btn-primary" onClick={handleCitationLog}>Log Citation Event</button>
          </div>
        </div>
      </div>

      <div className="terminal-grid p-6">
        <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Best SEO + AEO Creation Pointers (2026 trend-aware)</h3>
        <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>- Always include one answer-first summary block for AI Overview extraction and chatbot snippets.</li>
          <li>- Pair FAQs with evidence links; AI systems now reward verifiable claims more than broad copy.</li>
          <li>- Include structured comparison tables with dimensions users query in natural language prompts.</li>
          <li>- Keep page freshness under {settings.freshness_days} days for competitive tool pages.</li>
          <li>- Track platform-level citation rate weekly and iterate headings/entities from low-citation queries.</li>
        </ul>
      </div>

      <div className="terminal-grid p-6">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>AEO Citation Overview</h3>
          <InfoTag label="Citation metrics" description="Aggregate view of citation totals, unique sources, and highest-cited pages for AEO performance" variant="primary" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {[
            { label: 'ChatGPT citation rate', value: citationStats?.summary?.chatgpt?.citationRate || 0 },
            { label: 'Perplexity citation rate', value: citationStats?.summary?.perplexity?.citationRate || 0 },
            { label: 'AI Overview citation rate', value: citationStats?.summary?.['ai-overview']?.citationRate || 0 },
            { label: 'Copilot citation rate', value: citationStats?.summary?.copilot?.citationRate || 0 },
          ].map((item) => (
            <div key={item.label} className="terminal-grid p-4">
              <p className="text-2xl font-semibold" style={{ color: 'var(--accent-primary)', fontFamily: 'JetBrains Mono, monospace' }}>
                {item.value}%
              </p>
              <p className="text-xs uppercase tracking-[0.12em]" style={{ color: 'var(--text-muted)' }}>{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="terminal-grid p-6">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Query Tracking Table</h3>
          <InfoTag label="Query tracking" description="Monitor which AI search queries your content appears in and track citation frequency per query" variant="primary" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b" style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-muted)' }}>
                <th className="py-2 pr-3">Query</th>
                <th className="py-2 pr-3">ChatGPT</th>
                <th className="py-2 pr-3">Perplexity</th>
                <th className="py-2 pr-3">AI Overviews</th>
                <th className="py-2 pr-3">Copilot</th>
              </tr>
            </thead>
            <tbody>
              {queryRows.map((row) => (
                <tr key={row.query} className="border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="py-2 pr-3" style={{ color: 'var(--text-primary)' }}>{row.query}</td>
                  <td className="py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>{row.chatgpt}%</td>
                  <td className="py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>{row.perplexity}%</td>
                  <td className="py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>{row.aiOverview}%</td>
                  <td className="py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>{row.copilot}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="terminal-grid p-6">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Content AEO Audit</h3>
          <InfoTag label="Content audit" description="Bulk audit published pages for AEO score including freshness, schema, FAQ presence, and tool links" variant="primary" />
        </div>
        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b" style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-muted)' }}>
                <th className="py-2 pr-3">Page</th>
                <th className="py-2 pr-3">Score</th>
                <th className="py-2 pr-3">Fresh</th>
                <th className="py-2 pr-3">FAQ</th>
                <th className="py-2 pr-3">Table</th>
                <th className="py-2 pr-3">Notes</th>
              </tr>
            </thead>
            <tbody>
              {scores.slice(0, 250).map((item) => (
                <tr key={`${item.pageType}-${item.slug}`} className="border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="py-2 pr-3" style={{ color: 'var(--text-primary)' }}>{item.pageType}/{item.slug}</td>
                  <td className="py-2 pr-3" style={{ color: statusColor(item.score) }}>{item.score}</td>
                  <td className="py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>{item.isFresh ? 'Yes' : 'No'}</td>
                  <td className="py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>{item.hasFaqSchema ? 'Yes' : 'No'}</td>
                  <td className="py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>{item.hasComparisonTable ? 'Yes' : 'No'}</td>
                  <td className="py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>{item.notesUpdatedAt ? 'Attached' : 'Missing'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="terminal-grid p-6">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Freshness Risk: Tools older than 90 days</h3>
          <InfoTag label="Stale content alert" description="Tools not updated within the freshness threshold lose AEO ranking signals - review and refresh these entries" variant="warning" />
        </div>
        <div className="space-y-2 max-h-72 overflow-auto">
          {staleTools.length === 0 && <p style={{ color: 'var(--text-secondary)' }}>No stale tools found.</p>}
          {staleTools.slice(0, 200).map((tool) => (
            <div key={tool.id || tool.slug} className="terminal-grid p-3 flex items-center justify-between">
              <div>
                <p style={{ color: 'var(--text-primary)' }}>{tool.name}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>/tools/{tool.slug}</p>
              </div>
              <p className="text-sm" style={{ color: 'var(--accent-primary)' }}>
                {tool.days_since_update ? `${tool.days_since_update} days` : 'Missing updated_at'}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AEODashboard;
